import type {
  BackupPolicySecurityConfig,
  Condition,
  Configuration,
  DetectorType,
  ExclusionPatternFilterConditions,
  FindingObjectType,
  SecurityFindingsFilterConditions,
  SecurityScanDetectorTypeEnum,
} from '@repo/api-gw-sdk';
import {
  CreateFindingExclusionPatternApi,
  DeleteFindingExclusionPatternApi,
  GetSecurityPlanApi,
  IsSecurityPlanEnabledApi,
  ListExclusionPatternResourcesApi,
  ListFindingExclusionPatternsApi,
  SaveSecurityPlanApi,
  SecurityScansApi,
  UpdateFindingExclusionPatternApi,
  UpdateSecurityScanPoliciesConfigApi,
} from '@repo/api-gw-sdk';

import { collectValueConditions } from '@/types/advanceFilter';
import { convertQuery } from '@/utils/conditionQuery';
import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const baseUrl = '/security';

export const security = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const securityScansApi = new SecurityScansApi(config);
  const getSecurityPlanApi = new GetSecurityPlanApi(config);
  const isSecurityPlanEnabledApi = new IsSecurityPlanEnabledApi(config);
  const saveSecurityPlanApi = new SaveSecurityPlanApi(config);
  const updateSecurityScanPoliciesConfigApi =
    new UpdateSecurityScanPoliciesConfigApi(config);
  const listFindingExclusionPatternsApi = new ListFindingExclusionPatternsApi(
    config
  );
  const createFindingExclusionPatternApi = new CreateFindingExclusionPatternApi(
    config
  );
  const deleteFindingExclusionPatternApi = new DeleteFindingExclusionPatternApi(
    config
  );
  const updateFindingExclusionPatternApi = new UpdateFindingExclusionPatternApi(
    config
  );
  const listExclusionPatternResourcesApi = new ListExclusionPatternResourcesApi(
    config
  );

  return {
    useScansSummary: (
      resourceId: string,
      detectorType?: SecurityScanDetectorTypeEnum
    ) =>
      httpClient.execute(
        securityScansApi.getSecurityScansSummary.bind(securityScansApi),
        baseUrl,
        'getScansSummary',
        currentProjectId,
        resourceId,
        detectorType
      ),
    useCountInfectedResources: () =>
      httpClient.execute(
        securityScansApi.countInfectedResources.bind(securityScansApi),
        baseUrl,
        'count',
        currentProjectId
      ),
    excludeResource: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: true,
        }
      ),
    cancelResourceExclusion: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: false,
        }
      ),
    updatePolicies: (policies: BackupPolicySecurityConfig[]) =>
      updateSecurityScanPoliciesConfigApi.updateSecurityScanPoliciesConfig(
        currentProjectId,
        { policiesConfig: policies }
      ),
    useIsSecurityPlanEnabled: () =>
      httpClient.execute(
        isSecurityPlanEnabledApi.isSecurityPlanEnabled.bind(
          isSecurityPlanEnabledApi
        ),
        baseUrl,
        'isSecurityPlanEnabled',
        currentProjectId
      ),
    useSecurityPlan: () =>
      httpClient.execute(
        getSecurityPlanApi.getSecurityPlan.bind(getSecurityPlanApi),
        baseUrl,
        'getSecurityPlan',
        currentProjectId
      ),
    saveSecurityPlan: (
      condition: Condition | undefined,
      include: string[] = [],
      exclude: string[] = []
    ) =>
      saveSecurityPlanApi.saveSecurityPlan(currentProjectId, {
        condition,
        include,
        exclude,
      }),
    infectedSnapshots: {
      useList: (resourceId: string | undefined) =>
        httpClient.execute(
          securityScansApi.listInfectedSnapshots.bind(securityScansApi),
          resourceId ? baseUrl : null,
          'infectedSnapshots',
          currentProjectId,
          resourceId ?? ''
        ),
    },
    findings: {
      useList: (params: {
        enabled?: boolean;
        condition?: Condition;
        from?: string; // ISO date – include snapshots on/after this date
        to?: string; // ISO date – include snapshots on/before this date
        pageIndex?: number;
        pageSize?: number;
      }) => {
        const from = params.from ? dayjs.utc(params.from).toDate() : undefined;
        const to = params.to ? dayjs.utc(params.to).toDate() : undefined;
        const filters: SecurityFindingsFilterConditions = {
          ...convertQuery<SecurityFindingsFilterConditions>(params.condition),
          ...(from || to
            ? {
                snapshotTime: {
                  ...(from ? { _from: from } : {}),
                  ...(to ? { to } : {}),
                },
              }
            : {}),
        };

        const pageSize = params.pageSize ?? 50;
        const pageIndex = params.pageIndex ?? 0;
        const pageToken = pageIndex > 0 ? btoa(`page|${pageIndex}`) : undefined;

        return httpClient.execute(
          securityScansApi.listSecurityFindings.bind(securityScansApi),
          params.enabled === false ? null : baseUrl,
          'listFindings',
          currentProjectId,
          pageSize,
          pageToken,
          { filters }
        );
      },
    },
    pathExclusion: {
      usePathExclusion: ({
        condition,
        pageIndex,
        pageSize = 50,
      }: {
        condition?: Condition;
        pageIndex: number;
        pageSize?: number;
      }) => {
        const scopeCond = condition
          ? collectValueConditions(condition).find(
              (c) => c.property === 'scope'
            )
          : undefined;
        const withoutScope = condition
          ? {
              ...condition,
              conditions: condition.conditions?.filter(
                (c) => c.property !== 'scope'
              ),
            }
          : undefined;
        const base =
          convertQuery<ExclusionPatternFilterConditions>(withoutScope) ?? {};
        const hasGlobal = scopeCond?.value?.includes('Global');
        const hasResource = scopeCond?.value?.includes('Resource');
        if (hasGlobal && !hasResource) {
          base.resourceId = { ...base.resourceId, isBlank: true };
        }
        if (hasResource && !hasGlobal) {
          base.resourceId = { ...base.resourceId, isBlank: false };
        }
        const filters = Object.keys(base).length ? base : undefined;

        const pageToken = pageIndex > 0 ? btoa(`page|${pageIndex}`) : undefined;

        return httpClient.execute(
          listFindingExclusionPatternsApi.listFindingExclusionPatterns.bind(
            listFindingExclusionPatternsApi
          ),
          baseUrl,
          'getPathsExclusion',
          currentProjectId,
          pageSize,
          pageToken,
          filters ? { filters } : undefined
        );
      },
      useExclusionPatternResources: () => {
        return httpClient.execute(
          listExclusionPatternResourcesApi.listExclusionPatternResources.bind(
            listExclusionPatternResourcesApi
          ),
          baseUrl,
          'getExclusionPatternResources',
          currentProjectId
        );
      },
      create: (
        path: string,
        type: FindingObjectType,
        detector: DetectorType,
        providerResourceId?: string
      ) => {
        return createFindingExclusionPatternApi.createFindingExclusionPattern(
          currentProjectId,
          { path, type, detector, providerResourceId }
        );
      },
      update: (id: string, path: string, providerResourceId?: string) => {
        return updateFindingExclusionPatternApi.updateFindingExclusionPattern(
          currentProjectId,
          { id, path, providerResourceId }
        );
      },
      delete: (id: string) => {
        return deleteFindingExclusionPatternApi.deleteFindingExclusionPattern(
          currentProjectId,
          id
        );
      },
    },
  };
};
